/**
 * 爆款视频反推服务 - 基于火山方舟视频理解（Chat Completions 多模态）
 * 文档: https://www.volcengine.com/docs/82379/1895586 视频理解
 * 通过文本/对话模型 + 视频输入，反推分镜与提示词
 */

import { getTextModelConfig, type TextModelConfig } from './storyboard';
import { alibabaAI, modelAPI } from './api';

const DEFAULT_BASE_URL = 'https://ark.cn-beijing.volces.com/api/v3';

function supportsMultimodal(modelName?: string, modelType?: string): boolean {
  const name = (modelName || '').toLowerCase();
  const type = (modelType || '').toLowerCase();
  if (['vision', 'vl', 'multimodal', 'video-understanding', 'video_understanding'].includes(type)) {
    return true;
  }
  // Seed 对话模型（如 doubao-seed-2-0-pro-*）支持图/视频理解；不含 seedance 视频生成模型
  if (/doubao-seed-\d/.test(name)) {
    return true;
  }
  // 通义千问多模态：Qwen3.8-Flash 等支持图像/文本/视频理解
  // 文档: https://www.qianwenai.com/models/qwen3.8-flash
  if (
    name.includes('qwen3.8') ||
    name.includes('qwen3-8') ||
    /qwen[\w.-]*flash/.test(name) ||
    /qwen[\w.-]*vl/.test(name) ||
    name.includes('qwen-omni') ||
    name.includes('qwen2.5-vl') ||
    name.includes('qwen2-vl')
  ) {
    return true;
  }
  return (
    name.includes('vision') ||
    name.includes('vl') ||
    name.includes('doubao-vision') ||
    name.includes('qwen-vl') ||
    name.includes('gpt-4o') ||
    name.includes('gemini') ||
    name.includes('seed-1-6') ||
    name.includes('seed-2-0')
  );
}

export interface ViralReversePrompt {
  id: number;
  scene: string;
  timeRange: string;
  cameraWork: string;
  prompt: string;
}

const SYSTEM_PROMPT = `你是一个视频分镜反推助手。用户会上传一段视频，请仔细观看后完成以下任务：

1. 将视频按镜头/场景拆分为多个分镜（建议每个分镜 3-5 秒，至少 2 个分镜）。
2. 对每个分镜输出四项信息，便于用户复刻视频风格、用于视频生成：
   - 场景名：简短概括（如「开场镜头」「情绪高潮」）
   - 时间范围：如「0-4秒」「4-8秒」
   - 镜头运用：景别与运镜（如「航拍，缓慢下降」「近景，升格」）
   - 提示词：该镜头的详细画面描述，可用于视频生成（1-3 句话，画面感强）

输出格式：必须严格按以下格式，每个分镜一行，字段用制表符或竖线分隔：
场景名\\t时间范围\\t镜头运用\\t提示词

示例：
开场镜头	0-4秒	航拍，缓慢下降	城市天际线全景，金色阳光洒在玻璃幕墙上，镜头缓慢下降，营造宏大氛围。
人物出场	4-8秒	中景，跟拍	主角自信地走进现代化办公大厅，阳光从落地窗洒入，镜头跟随移动。

注意：只输出分镜表格行，不要其他解释；时间范围覆盖整段视频；提示词要具体、可复刻。`;

/**
 * 从后台获取文本/对话模型配置（与 AI 分镜共用，支持视频理解）
 */
export async function getVideoUnderstandingModelConfig(): Promise<TextModelConfig | null> {
  try {
    const models = await modelAPI.getAllModels();
    const active = (models || []).filter((m: any) => m?.is_active);
    const multimodal = active
      .filter((m: any) => supportsMultimodal(m.model_name, m.type))
      .sort((a: any, b: any) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
    if (multimodal.length > 0) return multimodal[0] as TextModelConfig;
  } catch (error) {
    console.error('获取多模态模型配置失败:', error);
  }
  // 兜底：保持与原逻辑兼容
  return getTextModelConfig();
}

/**
 * 调用视频理解 API，反推分镜与提示词
 * @param videoUrl 视频地址：公网 https 或 data:video/mp4;base64,...（建议视频不超过 20MB）
 */
export async function analyzeVideoForPrompts(
  videoUrl: string,
  config: TextModelConfig | null
): Promise<ViralReversePrompt[]> {
  if (!videoUrl?.trim()) return [];

  if (!config?.model_name) {
    throw new Error('请先在后台配置并启用支持视频理解的文本/多模态模型');
  }
  if (config.provider !== 'alibaba' && !config.api_key) {
    throw new Error('请先在后台配置模型 API Key');
  }
  if (!supportsMultimodal(config.model_name, config.type)) {
    throw new Error(
      `当前模型 ${config.model_name} 不支持多模态输入（image_url/video_url），请在后台启用支持视觉/视频理解的模型后重试`
    );
  }

  const userContent: Array<{ type: string; text?: string; video_url?: { url: string } }> = [
    { type: 'video_url', video_url: { url: videoUrl.trim() } },
    { type: 'text', text: '请对该视频进行分镜反推，按要求的格式输出每个镜头的场景名、时间范围、镜头运用和提示词。' },
  ];

  let content: string | undefined;

  if (config.provider === 'alibaba') {
    const data = await alibabaAI.chat(
      [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: userContent },
      ],
      { max_tokens: 4096, model_id: config.id }
    );
    content = data?.choices?.[0]?.message?.content?.trim();
  } else {
    const baseUrl = (config.base_url || DEFAULT_BASE_URL).replace(/\/$/, '');
    const url = `${baseUrl}/chat/completions`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.api_key}`,
      },
      body: JSON.stringify({
        model: config.model_name,
        messages: [
          { role: 'system', content: SYSTEM_PROMPT },
          { role: 'user', content: userContent },
        ],
        max_tokens: 4096,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        (err as { error?: { message?: string } })?.error?.message ||
          `视频理解请求失败 ${response.status}`
      );
    }

    const data = (await response.json()) as {
      choices?: Array<{ message?: { content?: string } }>;
    };
    content = data.choices?.[0]?.message?.content?.trim();
  }

  if (!content) {
    throw new Error('模型未返回分镜内容，请重试或换一段视频');
  }

  const parsed = parseReverseOutput(content);
  if (parsed.length === 0) {
    throw new Error('未能解析出分镜列表，请重试或换一段视频');
  }
  return parsed;
}

/**
 * 使用关键帧图片进行分镜反推（更稳定，避免超大 video data URL 导致 500）
 */
export async function analyzeFramesForPrompts(
  frameUrls: string[],
  config: TextModelConfig | null
): Promise<ViralReversePrompt[]> {
  if (!Array.isArray(frameUrls) || frameUrls.length === 0) return [];
  if (!config?.model_name) {
    throw new Error('请先在后台配置并启用文本/多模态模型，才能使用爆款视频反推');
  }
  if (config.provider !== 'alibaba' && !config.api_key) {
    throw new Error('请先在后台配置模型 API Key');
  }
  if (!supportsMultimodal(config.model_name, config.type)) {
    throw new Error(
      `当前模型 ${config.model_name} 不支持多模态输入（image_url/video_url），请在后台启用支持视觉/视频理解的模型后重试`
    );
  }

  const userContent: Array<{ type: string; text?: string; image_url?: { url: string } }> = [
    ...frameUrls.map((url) => ({ type: 'image_url' as const, image_url: { url } })),
    {
      type: 'text',
      text: '以上是同一段视频按时间顺序抽取的关键帧，请进行分镜反推，按要求格式输出每个镜头的场景名、时间范围、镜头运用和提示词。',
    },
  ];

  let content: string | undefined;

  if (config.provider === 'alibaba') {
    const data = await alibabaAI.chat(
      [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: userContent },
      ],
      { max_tokens: 4096, model_id: config.id }
    );
    content = data?.choices?.[0]?.message?.content?.trim();
  } else {
    const baseUrl = (config.base_url || DEFAULT_BASE_URL).replace(/\/$/, '');
    const url = `${baseUrl}/chat/completions`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${config.api_key}`,
      },
      body: JSON.stringify({
        model: config.model_name,
        messages: [
          { role: 'system', content: SYSTEM_PROMPT },
          { role: 'user', content: userContent },
        ],
        max_tokens: 4096,
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(
        (err as { error?: { message?: string } })?.error?.message ||
          `关键帧反推请求失败 ${response.status}`
      );
    }

    const data = (await response.json()) as {
      choices?: Array<{ message?: { content?: string } }>;
    };
    content = data.choices?.[0]?.message?.content?.trim();
  }

  if (!content) {
    throw new Error('模型未返回分镜内容，请重试或换一段视频');
  }

  const parsed = parseReverseOutput(content);
  if (parsed.length === 0) {
    throw new Error('未能解析出分镜列表，请重试或换一段视频');
  }
  return parsed;
}

/**
 * 解析模型输出为分镜列表
 * 支持：场景名\t时间范围\t镜头运用\t提示词（或竖线 | 分隔）
 */
function parseReverseOutput(text: string): ViralReversePrompt[] {
  const lines = text
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const result: ViralReversePrompt[] = [];
  const separator = /\t|\|/;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const parts = line.split(separator).map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 4) {
      result.push({
        id: result.length + 1,
        scene: parts[0],
        timeRange: parts[1],
        cameraWork: parts[2],
        prompt: parts.slice(3).join(' '),
      });
    } else if (parts.length === 3) {
      result.push({
        id: result.length + 1,
        scene: parts[0],
        timeRange: parts[1],
        cameraWork: '',
        prompt: parts[2],
      });
    }
  }
  return result;
}
